import { Modal } from "./Modal";
import { Property } from "global-types";

interface PropertyDetailsModalProps {
    property: Property | null;
    open: boolean;
    onClose: () => void;
}

export const PropertyDetailsModal: React.FC<PropertyDetailsModalProps> = ({
    property,
    open,
    onClose,
}) => {
    if (!property) return null;

    const formatDate = (dateString?: string) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const formatCurrency = (amount?: number) => {
        if (amount == null || typeof amount !== 'number') return "-";
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(amount);
    };

    const formatNumber = (num?: number) => {
        if (num == null || typeof num !== 'number') return "-";
        return num.toLocaleString();
    };

    const formatPercentage = (num?: number) => {
        if (num == null || typeof num !== 'number') return "-";
        return `${num.toFixed(2)}%`;
    };

    const fullAddress = [
        property.address,
        property.city,
        property.state,
        property.zip_code,
    ]
        .filter(Boolean)
        .join(", ");

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Property Details"
            description={`Detailed information for ${property.property_name || property.property_identifier}`}
            className="sm:max-w-4xl"
        >
            <div className="space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Basic Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">Basic Information</h3>
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-600">Property ID</label>
                            <p className="text-sm">{property.id}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Property Name</label>
                            <p className="text-sm">{property.property_name || "-"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Property Identifier</label>
                            <p className="text-sm">{property.property_identifier || "-"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Portfolio ID</label>
                            <p className="text-sm">{property.portfolio_id || "-"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Portfolio Name</label>
                            <p className="text-sm">{property.portfolio_name || "-"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Account Name</label>
                            <p className="text-sm">{property.account_name || "-"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Account ID</label>
                            <p className="text-sm">{property.account_id || "-"}</p>
                        </div>
                    </div>
                </div>

                {/* Address Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">Address</h3>
                    <div>
                        <label className="text-sm font-medium text-gray-600">Full Address</label>
                        <p className="text-sm">{fullAddress || "-"}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-600">Latitude</label>
                            <p className="text-sm">{typeof property.lat === 'number' ? property.lat.toFixed(6) : "-"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Longitude</label>
                            <p className="text-sm">{typeof property.lon === 'number' ? property.lon.toFixed(6) : "-"}</p>
                        </div>
                    </div>
                </div>

                {/* Property Details */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">Property Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-600">Total Units</label>
                            <p className="text-sm">{formatNumber(property.total_units)}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Year Built</label>
                            <p className="text-sm">{property.year_built || "-"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Active Tenants</label>
                            <p className="text-sm">{formatNumber(property.active_tenants)}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Occupancy Rate</label>
                            <p className="text-sm">{formatPercentage(property.occupancy_rate)}</p>
                        </div>
                    </div>
                </div>

                {/* Financial Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">Financial Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-600">Purchase Price</label>
                            <p className="text-sm">{formatCurrency(property.purchase_price)}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Selling Price</label>
                            <p className="text-sm">{formatCurrency(property.selling_price)}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Average Rent</label>
                            <p className="text-sm">{formatCurrency(property.average_rent)}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Monthly Revenue</label>
                            <p className="text-sm">{formatCurrency(property.monthly_revenue)}</p>
                        </div>
                    </div>
                </div>

                {/* Deal Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">Deal Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-600">Deal Start Date</label>
                            <p className="text-sm">{formatDate(property.deal_start_date)}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Deal Sold Date</label>
                            <p className="text-sm">{formatDate(property.deal_sold_date)}</p>
                        </div>
                    </div>
                </div>

                {/* Status Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">Status Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-600">Comps Status</label>
                            <p className="text-sm">{property.last_comps_status || "-"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Comps Last Run</label>
                            <p className="text-sm">{formatDate(property.last_comps_run_at)}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Renewals Status</label>
                            <p className="text-sm">{property.last_renewals_status || "-"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Renewals Last Run</label>
                            <p className="text-sm">{formatDate(property.last_renewals_run_at)}</p>
                        </div>
                    </div>
                </div>

                {/* Additional Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">Additional Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-600">Image URL</label>
                            <p className="text-sm break-all">{property.image_url || "-"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Hello Data Property ID</label>
                            <p className="text-sm">{property.hello_data_property_id || "-"}</p>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
